import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import {
  MessageAlertDismissed,
  SubscribeToBorrowerMessages,
  ThreadReply,
  SendMessage,
  GetMessageThreadFullHistory
} from '../message-store/message.actions';
import { Thread, Message } from '../message-service/message.model';
import { MessageState } from '../message-store/message.state';
import { Alert } from 'app/components/alert/alert.interface';
import { ActivityViewsService } from 'app/modules/funnel/models/activity-view.service';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { ApiHttpClient } from 'app/http/clients/api';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { LenderUser } from 'app/models/dto/lender-user.model';

interface Borrower {
  id: number;
  userId: number;
  name: string;
  lenderId: number;
  assignedLenderName: string;
  status: string;
  affiliateId: number | null;
  partnerGroup: string | null;
  primary_contact: {
    email: string;
    first: string;
    last: string;
  };
}

// Remove this component upon the deprecation of Inbox
@Component({
  selector: 'app-message-threads',
  templateUrl: './message-threads.component.html',
  styleUrls: ['./message-threads.component.scss']
})
export class MessageThreadsComponent implements OnInit, OnDestroy {

  @Input() borrower: Borrower;
  @Input() read: any;

  @Select(MessageState.threads) threads$: Observable<Thread[]>;
  @Select(MessageState.alert) alert$: Observable<Alert>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  complianceReview$: Observable<BorrowerValue | null>;
  inComplianceReview = false;
  showEditor = false;
  loadingInitial$: Observable<boolean>;
  loadingAll$: Observable<boolean>;
  disableLoadAll$: Observable<boolean>;
  hubspotEmailLoading = false;
  canRefreshHubspotEmails = false;
  destroyed$ = new Subject<void>();

  constructor(
    private store: Store,
    private activityViewsService: ActivityViewsService,
    private http: ApiHttpClient
  ) {}

  ngOnInit() {
    this.lenderUser$.pipe(takeUntil(this.destroyed$)).subscribe(currentUser => {
      this.canRefreshHubspotEmails = currentUser?.permissions.includes('refreshHubspotEmails');

      if (this.borrower?.userId === currentUser?.id) {
        this.activityViewsService.updateOrCreate(this.borrower.id, 'message').pipe(
          catchError(() => {
            return of();
          })
        ).subscribe();
      }
    });

    this.store.dispatch(new SubscribeToBorrowerMessages(this.borrower.id));

    this.loadingInitial$ = this.store.select(state => state.message.loadingInitial);

    this.loadingAll$ = this.store.select(state => state.message.loadingAll);
    this.disableLoadAll$ = this.store.select(state => state.message.disableLoadAll);

    this.complianceReview$ = this.store.select(BorrowerValueState.valueByAlias)
      .pipe(map(filterFn => filterFn('complianceReview')));

    this.complianceReview$.subscribe(complianceReview => {
      this.inComplianceReview = complianceReview && complianceReview.value !== null ? true : false;
    });
  }

  handleAlertDismissed() {
    this.store.dispatch(new MessageAlertDismissed());
  }

  loadAllThreads(): void {
    this.store.dispatch(new GetMessageThreadFullHistory(this.borrower.id));
  }

  trackById(model: any) {
    return model.id;
  }

  handleEmailCreate(message: Message): void {
    message.threadId ? this.store.dispatch(new ThreadReply(message.threadId, message)) : this.store.dispatch(new SendMessage(message));
  }

  startNewMessage(show: boolean) {
    this.showEditor = show;
  }

  pullHubspotEmails() {
    this.hubspotEmailLoading = true;
    this.http.get(`${environment.API_URL}/borrower/${this.borrower.id}/pull-hubspot-emails`)
      .pipe(take(1))
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }
}
